import { TaxIncentiveStatuses } from "@/prisma-package/src";
import Dinero from "dinero.js";
import { toCents } from "@/prisma-package/src/utils/to-cents.utils";
import { Maybe } from "../types/utility.types";

export type LedgerTotals = {
  qualifiedExpenses: number;
  nonQualifiedExpenses: number;
  totalExpenses: number;
  potentiallyQualifiedExpenses: number;
  pendingReviewExpenses: number;
  totalTransactions: number;
  totalQualifiedExpenseIncentive: number;
  totalPotentiallQualifiedExpenseIncentive: number;
};

export type StatusChangeParams = {
  currentTotals: LedgerTotals;
  amount: string;
  taxIncentiveAmount: Maybe<number>;
  previousStatus: Maybe<TaxIncentiveStatuses>;
  newStatus: TaxIncentiveStatuses;
};

/**
 * Recalculates ledger totals when a single entry's status changes.
 * This is an efficient incremental update that doesn't require re-processing all entries.
 *
 * @param params - The parameters for recalculation
 * @returns Updated ledger totals
 */
export const recalculateLedgerTotalsOnStatusChange = ({
  currentTotals,
  amount,
  taxIncentiveAmount,
  previousStatus,
  newStatus,
}: StatusChangeParams): LedgerTotals => {
  // If status didn't change, return current totals
  if (previousStatus === newStatus) {
    return currentTotals;
  }

  const amountCents = toCents(amount);
  const incentiveCents = taxIncentiveAmount ?? 0;

  // Convert current totals to cents for precise arithmetic
  const toCentValue = (dollars: number) =>
    Dinero({ amount: Math.round(dollars * 100), precision: 2 }).getAmount();

  let qualifiedExpensesCents = toCentValue(currentTotals.qualifiedExpenses);
  let nonQualifiedExpensesCents = toCentValue(
    currentTotals.nonQualifiedExpenses
  );
  let potentiallyQualifiedExpensesCents = toCentValue(
    currentTotals.potentiallyQualifiedExpenses
  );
  let pendingReviewExpensesCents = toCentValue(
    currentTotals.pendingReviewExpenses
  );
  let qualifiedIncentiveCents = toCentValue(
    currentTotals.totalQualifiedExpenseIncentive
  );
  let potentialQualifiedIncentiveCents = toCentValue(
    currentTotals.totalPotentiallQualifiedExpenseIncentive
  );

  // Subtract from previous status bucket
  switch (previousStatus) {
    case TaxIncentiveStatuses.QUALIFIED:
      qualifiedExpensesCents -= amountCents;
      qualifiedIncentiveCents -= incentiveCents;
      break;
    case TaxIncentiveStatuses.NOT_QUALIFIED:
      nonQualifiedExpensesCents -= amountCents;
      break;
    case TaxIncentiveStatuses.POTENTIALLY_QUALIFIED:
      potentiallyQualifiedExpensesCents -= amountCents;
      potentialQualifiedIncentiveCents -= incentiveCents;
      break;
    case TaxIncentiveStatuses.PENDING_REVIEW:
      pendingReviewExpensesCents -= amountCents;
      break;
  }

  // Add to new status bucket
  switch (newStatus) {
    case TaxIncentiveStatuses.QUALIFIED:
      qualifiedExpensesCents += amountCents;
      qualifiedIncentiveCents += incentiveCents;
      break;
    case TaxIncentiveStatuses.NOT_QUALIFIED:
      nonQualifiedExpensesCents += amountCents;
      break;
    case TaxIncentiveStatuses.POTENTIALLY_QUALIFIED:
      potentiallyQualifiedExpensesCents += amountCents;
      potentialQualifiedIncentiveCents += incentiveCents;
      break;
    case TaxIncentiveStatuses.PENDING_REVIEW:
      pendingReviewExpensesCents += amountCents;
      break;
  }

  // Convert cents to dollars
  const toDollars = (cents: number) =>
    Dinero({ amount: cents, precision: 2 }).toUnit();

  return {
    qualifiedExpenses: toDollars(qualifiedExpensesCents),
    nonQualifiedExpenses: toDollars(nonQualifiedExpensesCents),
    totalExpenses: currentTotals.totalExpenses, // Total doesn't change
    potentiallyQualifiedExpenses: toDollars(potentiallyQualifiedExpensesCents),
    pendingReviewExpenses: toDollars(pendingReviewExpensesCents),
    totalTransactions: currentTotals.totalTransactions, // Transaction count doesn't change
    totalQualifiedExpenseIncentive: toDollars(qualifiedIncentiveCents),
    totalPotentiallQualifiedExpenseIncentive: toDollars(
      potentialQualifiedIncentiveCents
    ),
  };
};
